import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import _ from "lodash";
import CustomAutocomplete from "../General/CustomAutocomplete";
import FileUploader from "../General/FileUploader";
import { HomeAppContext } from "../Home";
import api from "../../Services/api";
import { Grid, TextField, Button, Select, MenuItem, InputLabel, FormControl, Typography, CircularProgress, IconButton } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { ACTION_TYPES, useStyleGuide } from "./context/StyleGuideContext";
import ProductionMetadataSection from "./ProductionMetadata/ProductionMetadataSection";
import TmsMetadataSection from "./ProductionMetadata/Sections/TmsMetadataSection";
import ProjectAssistMetadataSection from "./ProductionMetadata/Sections/ProjectAssistMetadataSection";
import { getProvidersAsync } from "../../Services/providers";

export const ClientDesignFormMode = {
  NEW: "new",
  EDIT: "edit"
};

const productionSystemLabels = {
  Unknown: "Unknown",
  Tms: "TMS",
  ProjectAssist: "Project Assist"
};

const getProductionSystemForProvider = (providers, provider) => {
  const key = typeof provider === "string" ? provider.toLowerCase() : provider;
  if (!key || !Array.isArray(providers)) {
    return null;
  }

  const match = providers.find(
    (entry) => (entry?.key || entry?.id || "").toLowerCase() === key
  );
  return match?.productionSystem || null;
};

export default function Form({
  mode = ClientDesignFormMode.NEW,
  errors = [],
  setSaveDisabled,
  onInitialLoadChange
}) {
  const { state, dispatch } = useStyleGuide();
  const { entity, fileText, isUploading } = state;
  
  const { homeDispatch } = React.useContext(HomeAppContext);
  
  const [inputType, setInputType] = useState(entity.inputType || "text");
  const [file, setFile] = useState(entity.fileId ? [{ fileId: entity.fileId }] : null);
  const [fileCountRemaining, ] = useState(1);
  const [isUploadButtonEnabled, setIsUploadButtonEnabled] = useState(false);
  const [inputStatus, setInputStatus] = useState(false);
  const [fileDeletedStatus, setFileDeletedStatus] = useState(false);
  const [isClientDropdownDisabled, setIsClientDropdownDisabled] = useState(!entity.provider);
  const [providers, setProviders] = useState([]);
  const derivedProductionSystem = getProductionSystemForProvider(
    providers,
    entity.provider
  );
  const selectedProductionSystem =
    derivedProductionSystem || entity.productionStyleGuideType || "Unknown";
  const selectedProductionSystemLabel =
    productionSystemLabels[selectedProductionSystem] || selectedProductionSystem;
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [projectAssistLoading, setProjectAssistLoading] = useState(false);
  const [fileDetailsLoading, setFileDetailsLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(
    mode !== ClientDesignFormMode.EDIT
  );
  const fileLoadRequestedRef = useRef(false);
  const productionMetadata = entity.productionStyleGuideMetadata || {};
  const providerOptions = useMemo(() => {
    if (!Array.isArray(providers)) {
      return [];
    }

    return providers.filter((provider) => {
      if (provider?.isSelectable === false) {
        return provider?.key === entity.provider;
      }

      return true;
    });
  }, [providers, entity.provider]);

  useEffect(() => {
    let isMounted = true;

    const loadProviders = async () => {
      try {
        const data = await getProvidersAsync();
        if (isMounted) {
          setProviders(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (isMounted) {
          homeDispatch({
            type: "NOTIFICATION",
            data: {
              message: "Unable to load providers.",
              type: "error"
            }
          });
        }
      }
    };

    loadProviders();

    return () => {
      isMounted = false;
    };
  }, [homeDispatch]);

  useEffect(() => {
    if (typeof setSaveDisabled === "function") {
      setSaveDisabled(
        metadataLoading || projectAssistLoading || fileDetailsLoading
      );
    }
  }, [metadataLoading, projectAssistLoading, fileDetailsLoading, setSaveDisabled]);

  useEffect(() => {
    const mappedSystem = getProductionSystemForProvider(
      providers,
      entity.provider
    );
    if (mappedSystem && entity.productionStyleGuideType !== mappedSystem) {
      dispatch({
        type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
        payload: {
          field: "productionStyleGuideType",
          value: mappedSystem
        }
      });
      dispatch({
        type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
        payload: {
          field: "productionStyleGuideMetadata",
          value: {}
        }
      });
      dispatch({
        type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
        payload: {
          field: "productionStyleGuideId",
          value: null
        }
      });
    }
  }, [entity.provider, entity.productionStyleGuideType, providers, dispatch]);

  useEffect(() => {
    if (selectedProductionSystem !== "Tms" && metadataLoading) {
      setMetadataLoading(false);
    }
    if (selectedProductionSystem !== "ProjectAssist" && projectAssistLoading) {
      setProjectAssistLoading(false);
    }
  }, [selectedProductionSystem, metadataLoading, projectAssistLoading]);

  useEffect(() => {
    if (typeof onInitialLoadChange === "function") {
      onInitialLoadChange(mode === ClientDesignFormMode.EDIT);
    }
  }, [mode, onInitialLoadChange]);

  useEffect(() => {
    if (mode !== ClientDesignFormMode.EDIT) {
      return;
    }

    if (initialLoadComplete) {
      return;
    }

    if (mode === ClientDesignFormMode.EDIT && !entity?.id) {
      if (typeof onInitialLoadChange === "function") {
        onInitialLoadChange(true);
      }
      return;
    }

    const hasFile = !!entity?.fileId;

    if (hasFile && !fileLoadRequestedRef.current) {
      if (typeof onInitialLoadChange === "function") {
        onInitialLoadChange(true);
      }
      return;
    }

    const isBlockingInitialLoad =
      fileDetailsLoading || metadataLoading || projectAssistLoading;

    if (isBlockingInitialLoad) {
      if (typeof onInitialLoadChange === "function") {
        onInitialLoadChange(true);
      }
      return;
    }

    setInitialLoadComplete(true);
    if (typeof onInitialLoadChange === "function") {
      onInitialLoadChange(false);
    }
  }, [
    fileDetailsLoading,
    metadataLoading,
    projectAssistLoading,
    initialLoadComplete,
    mode,
    onInitialLoadChange,
    entity?.id,
    entity?.fileId
  ]);

  const handleTmsLoadingChange = useCallback(
    (isLoading) => setMetadataLoading(!!isLoading),
    []
  );

  const handleProjectAssistLoadingChange = useCallback(
    (isLoading) => setProjectAssistLoading(!!isLoading),
    []
  );

  const handleMetadataError = useCallback(
    (message) => {
      if (!message) return;
      homeDispatch({
        type: "NOTIFICATION",
        data: { message, type: "error" }
      });
    },
    [homeDispatch]
  );

  const extractId = useCallback((value) => {
    if (value === undefined || value === null) {
      return undefined;
    }

    if (typeof value === "object") {
      return (
        value.id ??
        value.Id ??
        value.clientId ??
        value.ClientId ??
        value.clientID ??
        value.ClientID ??
        value.value ??
        undefined
      );
    }

    return value;
  }, []);

  const normalizeOption = useCallback((option) => {
    if (option === undefined || option === null) {
      return option;
    }

    if (typeof option === "object") {
      const idValue = option.id ?? option.Id ?? option.value ?? option;
      const textValue =
        option.text ??
        option.Text ??
        option.name ??
        option.Name ??
        (idValue !== undefined ? `ID ${idValue}` : "");

      return {
        ...option,
        id: idValue,
        text: textValue
      };
    }

    return {
      id: option,
      text: `ID ${option}`
    };
  }, []);

  const handleMetadataChange = useCallback(
    (field, selection) => {
      const normalized = (selection || []).map((item) => normalizeOption(item));
      const nextMetadata = {
        ...(entity.productionStyleGuideMetadata || {}),
        [field]: normalized
      };

      dispatch({
        type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
        payload: { field: "productionStyleGuideMetadata", value: nextMetadata }
      });
    },
    [dispatch, normalizeOption, entity.productionStyleGuideMetadata]
  );

  const selectedVerticalIds = useMemo(() => {
    const ids =
      (productionMetadata.verticalIds || [])
        .map((value) => extractId(value))
        .filter((value) => value !== undefined && value !== null) || [];

    return Array.from(new Set(ids));
  }, [productionMetadata.verticalIds, extractId]);

  const selectedClientId = useMemo(() => {
    const firstClient = entity.clientDesignClients?.[0];
    const id = extractId(firstClient);

    if (id === undefined || id === null) {
      return null;
    }

    const normalized = typeof id === "string" ? id.trim() : id;
    if (normalized === "") {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? normalized : parsed;
  }, [entity.clientDesignClients, extractId]);

  useEffect(() => {
    setInputType(entity.inputType || "text");
    setIsClientDropdownDisabled(!entity.provider);

    if (entity.fileId) {
      setFile([{ fileId: entity.fileId }]);
      setFileDeletedStatus(false);
      setInputStatus(false);
      setIsUploadButtonEnabled(false);
    } else {
      setFile(null);
      setIsUploadButtonEnabled(false);
      setFileDeletedStatus(false);
      setInputStatus(entity.inputType === "file");
    }
  }, [entity.fileId, entity.inputType, entity.provider]);


  const onDesignTypeChange = (event) => {
    const newDesignType = event.target.value;
    
    if (newDesignType === 0) {
      dispatch({
        type: ACTION_TYPES.SET_ENTITY,
        payload: {
          ...entity,
          designType: newDesignType,
          language: ""
        }
      });
    } else {
      dispatch({
        type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
        payload: {
          field: "designType",
          value: newDesignType
        }
      });
    }
  };

  const onLanguageChange = (value) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
      payload: {
        field: "language",
        value
      }
    });
  };

  const onProviderChange = (event) => {
    const selectedProvider = event.target.value;
    setIsClientDropdownDisabled(!selectedProvider);
    const mappedProductionSystem =
      getProductionSystemForProvider(providers, selectedProvider) || "Unknown";

    dispatch({
      type: ACTION_TYPES.SET_ENTITY,
      payload: {
        ...entity,
        provider: selectedProvider,
        clientDesignClients: [],
        productionStyleGuideType: mappedProductionSystem,
        productionStyleGuideMetadata: {},
        productionStyleGuideId: null
      }
    });
  };

  const shouldShowOcrOptions = (fileName) => {
    if (!fileName) return false;
    const fileExtension = fileName.split('.').pop().toLowerCase();
    return fileExtension === 'docx';
  };
  
  const onFileUploaded = async (value) => {
    const uploadedFiles = value.map((item) => ({
      fileId: item.id,
      name: item.name
    }));
  
    if (uploadedFiles.length > 0) {
      setFile(uploadedFiles);
      
      dispatch({
        type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
        payload: {
          field: "fileId",
          value: uploadedFiles[0].fileId
        }
      });
      
      const fileName = uploadedFiles[0].name;
      const fileExtension = fileName.split('.').pop().toLowerCase();
      
      if (fileExtension === 'json') {
        dispatch({
          type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
          payload: {
            field: "ocrOption",
            value: "without-ocr"
          }
        });
        setIsUploadButtonEnabled(true);
      } else if (fileExtension === 'docx') {
        setIsUploadButtonEnabled(false);
      } else {
        checkIfUploadCanBeEnabled(uploadedFiles, entity.ocrOption);
      }
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileDeletedStatus(true);
    
    dispatch({
      type: ACTION_TYPES.SET_ENTITY,
      payload: {
        ...entity,
        fileId: 0,
        fileName: null,
        ocrOption: ""
      }
    });
    
    dispatch({
      type: ACTION_TYPES.SET_FILE_TEXT,
      payload: ""
    });
    
    setIsUploadButtonEnabled(false);
  };

  const handleInputTypeChange = (event) => {
    const newInputType = event.target.value;
    setInputType(newInputType);
    
    dispatch({
      type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
      payload: {
        field: "inputType",
        value: newInputType
      }
    });
    
    dispatch({
      type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
      payload: {
        field: "ocrOption",
        value: ""
      }
    });
    
    dispatch({
      type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
      payload: {
        field: "fileId",
        value: 0
      }
    });
    
    dispatch({
      type: ACTION_TYPES.SET_FILE_TEXT,
      payload: ""
    });

    setFile(null);
    setFileDeletedStatus(false);
    
    if (newInputType === "file") {
      setInputStatus(true);
    } else {
      setInputStatus(false);
    }
    
    setIsUploadButtonEnabled(false);
  };

  const handleOcrOptionChange = (event) => {
    const selectedOcrOption = event.target.value;
    
    dispatch({
      type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
      payload: {
        field: "ocrOption",
        value: selectedOcrOption
      }
    });
    
    checkIfUploadCanBeEnabled(file, selectedOcrOption);

    if (selectedOcrOption === "with-ocr") {
      homeDispatch({
        type: "NOTIFICATION",
        data: { 
          message: "With OCR: The file will be scanned to pull text from images. This process may take a few minutes.", 
          type: "info" 
        }
      });
    } else if (selectedOcrOption === "without-ocr") {
      homeDispatch({
        type: "NOTIFICATION",
        data: { 
          message: "Without OCR: The file will be uploaded without scanning for text in images. This process may be faster.", 
          type: "info" 
        }
      });
    }
  };

  const handlePriorityChange = (event) => {
    const parsed = Math.max(1, parseInt(event.target.value, 10) || 1);
    dispatch({
      type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
      payload: {
        field: "priority",
        value: parsed
      }
    });
  };

  const checkIfUploadCanBeEnabled = (uploadedFile, ocrOptionValue) => {
    if (uploadedFile && uploadedFile[0] && ocrOptionValue) {
      setIsUploadButtonEnabled(true);
    } else {
      setIsUploadButtonEnabled(false);
    }
  };

  const handleUploadFile = async () => {
    if (file && file[0]) {
      dispatch({
        type: ACTION_TYPES.SET_UPLOADING,
        payload: true
      });
  
      const params = {
        fileId: file[0].fileId,
        ocrOption: entity.ocrOption,
      };
  
      try {
        const response = await api.get("/api/files/process-file", {
          params: params
        });
  
        if (response.data && response.data.text) {
          const extractedText = response.data.text;
          
          dispatch({
            type: ACTION_TYPES.SET_FILE_TEXT,
            payload: extractedText
          });
          
          dispatch({
            type: ACTION_TYPES.SET_DESIGN_TEXT,
            payload: extractedText
          });   
          
          homeDispatch({
            type: "NOTIFICATION",
            data: { 
              message: "File processed successfully", 
              type: "success" 
            }
          });
        } else {
          homeDispatch({
            type: "NOTIFICATION",
            data: { 
              message: "File processing completed but no text was extracted", 
              type: "warning" 
            }
          });
        }
      } catch (error) {
        console.error("File processing failed:", error);
        homeDispatch({
          type: "NOTIFICATION",
          data: { 
            message: "Error processing file: " + (error.response?.data || error.message), 
            type: "error" 
          }
        });
      } finally {
        dispatch({
          type: ACTION_TYPES.SET_UPLOADING,
          payload: false
        });
      }
    }
  };

  const onClientChange = (value, property) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
      payload: {
        field: property,
        value
      }
    });
  };
  
  useEffect(() => {
    let isMounted = true;

    if (mode === ClientDesignFormMode.EDIT && entity.fileId) {
      fileLoadRequestedRef.current = true;
      setFileDetailsLoading(true);

      async function fetchFile() {
        try {
          const responseFile = await api.get(
            `/api/files/get-file?id=${entity.fileId}`
          );

          if (!isMounted) {
            return;
          }

          if (responseFile.data) {
            setFile([
              { fileId: responseFile.data.fileId, name: responseFile.data.name }
            ]);

            dispatch({
              type: ACTION_TYPES.SET_FILE_TEXT,
              payload: entity.design || ""
            });

            const fileExtension = responseFile.data.name
              .split(".")
              .pop()
              .toLowerCase();

            if (fileExtension === "docx") {
              checkIfUploadCanBeEnabled(
                [{ fileId: responseFile.data.fileId }],
                entity.ocrOption
              );
            } else if (fileExtension === "json") {
              setIsUploadButtonEnabled(true);
            } else {
              setIsUploadButtonEnabled(false);
            }
          } else {
            setIsUploadButtonEnabled(false);
          }
        } catch (error) {
          if (!isMounted) {
            return;
          }

          console.error("Error fetching file: ", error);
          homeDispatch({
            type: "NOTIFICATION",
            data: {
              message:
                "Error fetching file information: " +
                (error.response?.data || error.message),
              type: "error"
            }
          });
        } finally {
          if (isMounted) {
            setFileDetailsLoading(false);
          }
        }
      }
      fetchFile();
    } else {
      fileLoadRequestedRef.current = false;
      setFileDetailsLoading(false);
      if (!entity.fileId) {
        setFile(null);
        setIsUploadButtonEnabled(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [
    entity.fileId,
    mode,
    dispatch,
    entity.design,
    entity.ocrOption,
    homeDispatch
  ]);


  const handleDesignTextChange = (event) => {
    const newText = event.target.value;
    
    dispatch({
      type: ACTION_TYPES.SET_DESIGN_TEXT,
      payload: newText
    });
  };

  return entity === null ? null : (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <FormControl fullWidth>
          <InputLabel id="provider-label">Provider</InputLabel>
          <Select
            labelId="provider-label"
            id="provider"
            value={entity.provider}
            label="Provider"
            onChange={onProviderChange}
          >
            {providerOptions.map((provider) => (
              <MenuItem
                key={provider.key || provider.id || provider.name}
                value={provider.key || provider.id}
              >
                {provider.name || provider.key}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <CustomAutocomplete
          value={entity.clientDesignClients}
          setValue={(value) => onClientChange(value, "clientDesignClients")}
          related={null}
          onLoading={setSaveDisabled}
          url={`/api/clients/clients?provider=${entity.provider}`}
          label={"Clients"}
          placeholder={"Select a client"}
          noOptionsText={"No client found"}
          loadingText={"Searching"}
          margin={"normal"}
          multiple={true}
          liveOptions={true}
          disabled={isClientDropdownDisabled}
          error={_.isArray(errors.clientDesignClients)}
          helperText={
            _.isArray(errors.clientDesignClients) && errors.clientDesignClients.join()
          }
          disableClearable={true}
        />

        <TextField
          label="Production System"
          fullWidth
          margin="normal"
          value={selectedProductionSystemLabel}
          InputProps={{ readOnly: true }}
          disabled
        />

        <TextField
          label="Priority"
          type="number"
          fullWidth
          margin="normal"
          value={entity.priority ?? 1}
        onChange={handlePriorityChange}
        inputProps={{ min: 1 }}
      />

        <ProductionMetadataSection
          isLoading={metadataLoading || projectAssistLoading}
          selectedProductionSystem={selectedProductionSystem}
          renderSection={(system) => {
            if (system === "Tms") {
              return (
                <TmsMetadataSection
                  enabled
                  clientId={selectedClientId}
                  values={{
                    documentTypeIds: productionMetadata.documentTypeIds || [],
                    verticalIds: productionMetadata.verticalIds || [],
                    disciplineIds: productionMetadata.disciplineIds || [],
                    productLineIds: productionMetadata.productLineIds || []
                  }}
                  selectedVerticalIds={selectedVerticalIds}
                  onChange={handleMetadataChange}
                  onLoadingChange={handleTmsLoadingChange}
                  onError={handleMetadataError}
                />
              );
            }

            if (system === "ProjectAssist") {
              return (
                <ProjectAssistMetadataSection
                  enabled
                  values={{
                    projectAssistSpecializationIds:
                      productionMetadata.projectAssistSpecializationIds || [],
                    projectAssistContentTypeIds:
                      productionMetadata.projectAssistContentTypeIds || []
                  }}
                  onChange={handleMetadataChange}
                  onLoadingChange={handleProjectAssistLoadingChange}
                  onError={handleMetadataError}
                />
              );
            }

            return null;
          }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Type</InputLabel>
          <Select value={entity.designType} onChange={onDesignTypeChange}>
            <MenuItem value={0}>Generic</MenuItem>
            <MenuItem value={1}>Language Specific</MenuItem>
          </Select>
        </FormControl>

        {entity.designType === 1 && (
          <FormControl fullWidth margin="normal">
            <CustomAutocomplete
              value={entity.language}
              setValue={(value) => onLanguageChange(value)}
              related={null}
              onLoading={setSaveDisabled}
              url={"/api/language/GetAllLanguages"}
              label={"Language"}
              placeholder={"Select a language"}
              noOptionsText={"No language found"}
              loadingText={"Searching"}
              multiple={false}
              liveOptions={false}
              error={_.isArray(errors.dataset)}
              helperText={
                _.isArray(errors.dataset) && errors.dataset.join()
              }
              disableClearable={true}
            />
          </FormControl>
        )}

        <TextField
          label="Style Guide Name"
          fullWidth
          margin="normal"
          value={entity.name || ""}
          onChange={(event) => dispatch({
            type: ACTION_TYPES.UPDATE_ENTITY_FIELD,
            payload: {
              field: "name",
              value: event.target.value
            }
          })}
        />
        
        <FormControl fullWidth margin="normal">
          <InputLabel>Choose Input Type</InputLabel>
          <Select value={inputType} onChange={handleInputTypeChange}>
            <MenuItem value="text">Text Input</MenuItem>
            <MenuItem value="file">File Upload</MenuItem>
          </Select>
        </FormControl>

        {inputType === "text" ? (
          <TextField
            label="Style Guide Details"
            fullWidth
            multiline
            rows={9}
            value={entity.design || ""}
            onChange={handleDesignTextChange}
            margin="normal"
          />
        ) : inputType === "file" ? (
          <>
            {mode === ClientDesignFormMode.EDIT && !inputStatus && !fileDeletedStatus && file && file[0] && file[0].fileId > 0 ? (
              <div
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  padding: "16px",
                  marginTop: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#f5f5f5",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                }}
              >
                <div>
                  <Typography variant="subtitle1" style={{ fontWeight: "bold", color: "#333" }}>
                    File ID: {file[0].fileId}
                  </Typography>
                  <Typography variant="body2" style={{ color: "#555" }}>
                    File Name: {file[0].name}
                  </Typography>
                </div>

                <IconButton
                  color="secondary"
                  onClick={handleRemoveFile}
                  style={{
                    backgroundColor: "#e57373",
                    color: "white",
                    marginLeft: "auto",
                    borderRadius: "50%",
                    boxShadow: "0 3px 5px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            ) : (
              <>
                <FileUploader
                  onFileUploaded={onFileUploaded}
                  uploadUrl="/api/files"
                  acceptedFileExtensions=".docx, .json"
                  acceptedFileExtensionsMessage=".docx (Word documents), .json (JSON files)"
                  numberOfFilesCanBeUploaded={fileCountRemaining}
                  maxFiles={fileCountRemaining}
                  maxSize={536870912}
                  existingFiles={file}
                />
                {inputType === "file" && file && file[0] && shouldShowOcrOptions(file[0].name) && (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Choose OCR Option</InputLabel>
                    <Select value={entity.ocrOption} onChange={handleOcrOptionChange}>
                      <MenuItem value="with-ocr">With OCR</MenuItem>
                      <MenuItem value="without-ocr">Without OCR</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {file && file[0] && (
                  <Button
                    onClick={handleUploadFile}
                    variant="contained"
                    color="primary"
                    disabled={!isUploadButtonEnabled || isUploading}
                    startIcon={isUploading ? <CircularProgress size={20} /> : null}
                    sx={{ marginTop: "10px" }}
                  >
                    {isUploading ? "Processing..." : "Process File"}
                  </Button>
                )}
              </>
            )}
          </>
        ) : null}
      </Grid>

      <Grid item xs={6}>
        <Typography variant="h6">Live Preview</Typography>
        <Typography variant="body1" 
          sx={{
            whiteSpace: "pre-wrap",        
            wordWrap: "break-word",        
            padding: "10px",               
            maxWidth: "100%",              
            fontFamily: "Arial, sans-serif",
            fontSize: "14px",              
            lineHeight: "1.5",             
            border: "1px solid #ccc",      
            overflowY: "auto",             
            height: "400px",               
            boxSizing: "border-box",       
          }}
        >
          {inputType === "text" ? entity.design : fileText}
        </Typography>
      </Grid>
    </Grid>
  );
}
